import { useNavigate } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { AlertCircle, Home, RefreshCw } from "lucide-react";
import type { FallbackProps } from "./ErrorBoundary";

const isDev = import.meta.env.VITE_APP_ENV === "development";

/**
 * Root-level error fallback - Full page error (replaces entire app)
 * Used when the entire application crashes
 */
export function RootErrorFallback({ error }: FallbackProps) {
  return (
    <div className="h-screen w-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-2xl w-full space-y-6">
        <div className="flex flex-col items-center text-center space-y-4">
          <AlertCircle className="h-16 w-16 text-destructive" />
          <h1 className="text-3xl font-bold">Something went wrong</h1>
          <p className="text-muted-foreground">
            We encountered an unexpected error. This has been reported to our team.
          </p>
        </div>

        {isDev && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Development Error Details</AlertTitle>
            <AlertDescription>
              <p className="text-sm font-mono mt-2">{error.message}</p>
              {error.stack && (
                <pre className="text-xs mt-2 overflow-auto max-h-48 p-2 bg-muted rounded">
                  {error.stack}
                </pre>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-3 justify-center">
          <Button onClick={() => window.location.reload()} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Reload Page
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * App-level error fallback - Full page error (provider/routing failures)
 * Used when providers or routing fails
 */
export function AppErrorFallback({ error, resetError }: FallbackProps) {
  return (
    <div className="h-screen w-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-2xl w-full space-y-6">
        <div className="flex flex-col items-center text-center space-y-4">
          <AlertCircle className="h-16 w-16 text-destructive" />
          <h1 className="text-3xl font-bold">Unable to load application</h1>
          <p className="text-muted-foreground">
            We're having trouble loading the application. This has been reported to our team.
          </p>
        </div>

        {isDev && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Development Error Details</AlertTitle>
            <AlertDescription>
              <p className="text-sm font-mono mt-2">{error.message}</p>
              {error.stack && (
                <pre className="text-xs mt-2 overflow-auto max-h-48 p-2 bg-muted rounded">
                  {error.stack}
                </pre>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-3 justify-center">
          <Button onClick={() => (window.location.href = "/login")} className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            Go to Login
          </Button>
          <Button onClick={resetError} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Layout-level error fallback - Inline error (preserves sidebar and header)
 * This is the PRIMARY error boundary for most view errors
 */
export function LayoutErrorFallback({ error, resetError }: FallbackProps) {
  const navigate = useNavigate();

  return (
    <div className="p-6 flex items-center justify-center min-h-[400px]">
      <Alert variant="destructive" className="max-w-2xl">
        <AlertCircle className="h-5 w-5" />
        <AlertTitle className="text-lg font-semibold mb-2">Something went wrong</AlertTitle>
        <AlertDescription className="space-y-4">
          <p className="text-sm">
            We encountered an unexpected error while loading this page. This has been reported to our team.
          </p>

          {isDev && (
            <div className="mt-4 p-4 bg-muted rounded-md">
              <p className="text-sm font-semibold mb-2">Development Error Details:</p>
              <p className="text-xs font-mono">{error.message}</p>
              {error.stack && (
                <pre className="text-xs mt-2 overflow-auto max-h-48">
                  {error.stack}
                </pre>
              )}
            </div>
          )}

          <div className="flex gap-3 mt-4">
            <Button variant="default" onClick={() => navigate("/dashboard")} className="flex items-center gap-2">
              <Home className="h-4 w-4" />
              Go to Dashboard
            </Button>
            <Button variant="outline" onClick={resetError} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Try Again
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}

/**
 * Component-level error fallback - Minimal inline error
 * Used for isolated component failures
 */
export function ComponentErrorFallback({ error, resetError }: FallbackProps) {
  return (
    <Alert variant="destructive" className="my-4">
      <AlertCircle className="h-4 w-4" />
      <AlertTitle>Error loading component</AlertTitle>
      <AlertDescription className="space-y-2">
        <p className="text-sm">
          {isDev ? error.message : "This feature is temporarily unavailable. Please try again later."}
        </p>
        <Button variant="outline" size="sm" onClick={resetError} className="mt-2">
          <RefreshCw className="h-3 w-3 mr-2" />
          Try Again
        </Button>
      </AlertDescription>
    </Alert>
  );
}
